import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { fetchCalendars, deleteCalendar, cancelFetchCalendars } from './actions';
import { makeSelectCalendars, makeSelectLoading, makeSelectError, makeSelectDeleting } from './selectors';
import Table from 'atoms/Table';
import SmallLoadingIndicator from 'atoms/SmallLoadingIndicator';

// Define the shape of a single calendar object for type safety
interface Calendar {
  id: string;
  summary: string;
  patientName: string | null;
}

// Define the props for the component
interface GoogleCalendarManagerProps {
  calendars: Calendar[] | null;
  loading: boolean;
  deleting: boolean;
  error: string | null;
  fetchCalendars: () => void;
  deleteCalendar: (id: string) => void;
  cancelFetchCalendars: () => void;
}

const GoogleCalendarManager: React.FC<GoogleCalendarManagerProps> = ({
  calendars,
  loading,
  deleting,
  error,
  fetchCalendars,
  deleteCalendar,
  cancelFetchCalendars,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [calendarToDelete, setCalendarToDelete] = useState<Calendar | null>(null);
  const wasDeletingRef = useRef(false);

  useEffect(() => {
    fetchCalendars();
    // Return a cleanup function to be called when the component unmounts
    return () => {
      cancelFetchCalendars();
      setModalOpen(false);
      setCalendarToDelete(null);
    };
  }, [fetchCalendars, cancelFetchCalendars]);

  // Effect to handle closing the modal after deletion is complete
  useEffect(() => {
    if (wasDeletingRef.current && !deleting) {
      // Deletion has just finished, regardless of success or failure.
      // The saga is now responsible for showing the toast message.
      setModalOpen(false);
      setCalendarToDelete(null);
    }
    wasDeletingRef.current = deleting;
  }, [deleting]);

  const toggleModal = () => {
    if (!deleting) {
      setModalOpen(!isModalOpen);
      if (isModalOpen) {
        setCalendarToDelete(null);
      }
    }
  };

  const handleDeleteClick = (rowData: Calendar) => {
    setCalendarToDelete(rowData);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (calendarToDelete) {
      deleteCalendar(calendarToDelete.id);
    }
  };

  const columns = [
    {
      field: 'summary',
      title: 'Summary',
    },
    {
      field: 'patientName',
      title: 'Linked to Patient',
      render: (rowData: Calendar) => (
        <span style={{ textAlign: 'center', display: 'block' }}>
          {rowData.patientName !== null ? '✓' : '✗'}
        </span>
      ),
    },
    {
      field: 'id',
      title: 'Actions',
      render: (rowData: Calendar) => (
        <div>
          <a
            href={`https://calendar.google.com/calendar/embed?src=${encodeURIComponent(rowData.id)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-primary btn-color"
          >
            View
          </a>
          <button
            className="btn btn-sm btn-danger ml-2"
            onClick={() => handleDeleteClick(rowData)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <h5 className="pb-3">Google Calendar Management</h5>
      {loading && <SmallLoadingIndicator />}
      {error && !deleting && <p className="text-danger">Error: {error}</p>}
      <Table
        title="Google Calendars"
        data={calendars || []}
        columns={columns}
        placeholder="No calendars to display"
        showToolbar
      />
      {calendarToDelete && (
        <Modal
          className="modal-dialog-centered custom-modal"
          isOpen={isModalOpen}
          toggle={toggleModal}
        >
          <ModalHeader toggle={toggleModal}>Confirm Deletion</ModalHeader>
          <ModalBody>
            <span>
              Are you sure you want to delete the calendar: <b>{calendarToDelete.summary}</b>?
              <br />
              This action cannot be undone.
            </span>
            <div className="d-flex flex-row justify-content-between w-100 mt-4">
              <button
                className="btn btn-white bg-white btn-lg btn-block border mt-2 font-15 mr-2"
                onClick={toggleModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger btn-lg btn-block btn-color font-15 position-relative"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting && (
                  <SmallLoadingIndicator
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '25px',
                      height: '25px',
                    }}
                  />
                )}
                <span style={{ color: deleting ? 'transparent' : 'inherit' }}>
                  Confirm Delete
                </span>
              </button>
            </div>
            {error && wasDeletingRef.current && !deleting && (
              <p className="text-danger text-center mt-3">
                Failed to delete calendar: {error}
              </p>
            )}
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  calendars: makeSelectCalendars(),
  loading: makeSelectLoading(),
  deleting: makeSelectDeleting(),
  error: makeSelectError(),
});

const mapDispatchToProps = {
  fetchCalendars,
  deleteCalendar,
  cancelFetchCalendars,
};

export default connect(mapStateToProps, mapDispatchToProps)(GoogleCalendarManager);
